import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Leaf, ShieldCheck, ThumbsUp, Star, MapPin, ChevronRight, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { menuItems, combos, reviews, locations } from '../data/menuData';
import { useCart } from '../context/CartContext';
import { ProductCard, ProductCardSkeleton } from '../components/menu/ProductCard';
import { ProductDetail } from '../components/menu/ProductDetail';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const qualitySignals = [
  {
    icon: Leaf,
    title: 'Ingredientes frescos',
    description: 'Productos locales y de temporada seleccionados diariamente',
  },
  {
    icon: ShieldCheck,
    title: 'Higiene certificada',
    description: 'Protocolos de limpieza que superan los estándares',
  },
  {
    icon: Clock,
    title: 'Listo en minutos',
    description: 'Tiempo promedio de preparación: 15-20 min',
  },
  {
    icon: ThumbsUp,
    title: '98% satisfacción',
    description: 'Basado en más de 10,000 pedidos del último mes',
  },
];

const howItWorks = [
  { step: 1, title: 'Elige', description: 'Explora nuestro menú y selecciona tus favoritos' },
  { step: 2, title: 'Personaliza', description: 'Ajusta ingredientes, extras y preferencias' },
  { step: 3, title: 'Recibe', description: 'Entrega a domicilio o retira en sucursal' },
];

export default function HomePage() {
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { addItem, openCart, setLocation, selectedLocation } = useCart();

  const popularItems = menuItems.filter(item => item.isPopular).slice(0, 4);
  const featuredCombos = combos.slice(0, 3);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleQuickAdd = (item) => {
    if ((item.variants && item.variants.length > 0) || (item.extras && item.extras.length > 0)) {
      setSelectedProduct(item);
      return;
    }
    
    addItem({
      ...item,
      quantity: 1,
      selectedVariants: {},
      selectedExtras: [],
      note: '',
    });
    
    toast.success(`${item.name} agregado`, {
      action: {
        label: 'Ver carrito',
        onClick: () => openCart(),
      },
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4 bg-accent/10 text-accent hover:bg-accent/20">
                Entrega en 20-30 min
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
                Sabor que te hace{' '}
                <span className="text-accent">volver</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-lg">
                Ingredientes frescos, preparación artesanal y entrega rápida. Tu próxima comida favorita está a un clic.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/menu">
                  <Button 
                    size="lg" 
                    className="h-14 px-8 rounded-full text-base font-medium w-full sm:w-auto"
                    data-testid="hero-view-menu-btn"
                  >
                    Ver menú
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="h-14 px-8 rounded-full text-base font-medium w-full sm:w-auto"
                  data-testid="hero-reorder-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Reordenar
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80"
                  alt="Platos gourmet"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-lg border border-border/50 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-accent fill-accent" />
                  </div>
                  <div>
                    <p className="font-semibold">4.9 estrellas</p>
                    <p className="text-sm text-muted-foreground">+2,500 reseñas</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Signals */}
      <section className="py-16 md:py-20 border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {qualitySignals.map((signal, index) => (
              <motion.div
                key={signal.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center md:text-left"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto md:mx-0 mb-4">
                  <signal.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-medium text-base">{signal.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  {signal.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Items */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
                Más vendidos hoy
              </h2>
              <p className="text-muted-foreground mt-2">
                Lo que más piden nuestros clientes
              </p>
            </div>
            <Link 
              to="/menu" 
              className="hidden md:flex items-center gap-1 text-sm font-medium hover:text-accent transition-colors"
              data-testid="view-all-menu-link"
            >
              Ver todo el menú
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              Array(4).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)
            ) : (
              popularItems.map((item, index) => (
                <ProductCard
                  key={item.id}
                  product={item}
                  index={index}
                  onViewDetails={setSelectedProduct}
                />
              ))
            )}
          </div>

          <Link to="/menu" className="md:hidden mt-6 block">
            <Button variant="outline" className="w-full rounded-full">
              Ver todo el menú
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Promos / Combos */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Promos y combos
            </h2>
            <p className="text-muted-foreground mt-2">
              Más valor, mismo sabor
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {featuredCombos.map((combo, index) => (
              <motion.div
                key={combo.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group bg-card border border-border/50 overflow-hidden hover:shadow-lg transition-all"
                data-testid={`combo-${combo.id}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={combo.image}
                    alt={combo.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-accent text-accent-foreground">
                      Ahorra ${combo.savings}
                    </Badge>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-medium text-lg">{combo.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{combo.description}</p>
                  <ul className="mt-3 space-y-1">
                    {combo.includes.map((item, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="w-1 h-1 bg-accent rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                    <div>
                      <span className="text-lg font-semibold">${combo.price}</span>
                      <span className="text-sm text-muted-foreground line-through ml-2">
                        ${combo.originalPrice}
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => handleQuickAdd({
                        ...combo,
                        category: 'combo',
                        variants: [],
                        extras: [],
                      })}
                      data-testid={`add-combo-${combo.id}`}
                    >
                      Agregar
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Cómo funciona
            </h2>
            <p className="text-muted-foreground mt-2">
              Tu pedido en 3 simples pasos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-2xl font-heading font-medium">
                  {step.step}
                </div>
                <h3 className="font-medium text-xl mt-6">{step.title}</h3>
                <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-muted-foreground mt-2">
              Más de 2,500 reseñas con 4.9 estrellas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.slice(0, 4).map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-card p-6 border border-border/50"
                data-testid={`review-${review.id}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array(review.rating).fill(0).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-accent fill-accent" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed">"{review.comment}"</p>
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="font-medium text-sm">{review.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Pidió: {review.order}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearest Location */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
                Encuéntranos cerca de ti
              </h2>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                {locations.length} sucursales en Monterrey y área metropolitana. 
                Selecciona la más cercana para ver horarios y tiempo de entrega.
              </p>
              
              <div className="mt-6 space-y-3">
                {locations.slice(0, 3).map(loc => (
                  <button
                    key={loc.id}
                    onClick={() => {
                      setLocation(loc);
                      toast.success(`Sucursal ${loc.name} seleccionada`);
                    }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      selectedLocation?.id === loc.id
                        ? 'border-accent bg-accent/5'
                        : 'border-border/50 hover:border-accent/50'
                    }`}
                    data-testid={`location-${loc.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-accent mt-0.5" />
                        <div>
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {loc.address}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{loc.avgPrepTime} min</p>
                        <p className="text-xs text-muted-foreground">tiempo aprox.</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <Link to="/ubicaciones" className="mt-6 block">
                <Button variant="outline" className="rounded-full w-full md:w-auto">
                  Ver todas las ubicaciones
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* Map placeholder */}
            <div className="aspect-square md:aspect-[4/3] bg-secondary rounded-2xl overflow-hidden relative">
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&q=80"
                alt="Mapa de ubicaciones"
                className="w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-card p-6 rounded-xl shadow-lg text-center">
                  <MapPin className="w-8 h-8 text-accent mx-auto mb-2" />
                  <p className="font-medium">Mapa interactivo</p>
                  <p className="text-sm text-muted-foreground">Próximamente</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
            ¿Listo para ordenar?
          </h2>
          <p className="mt-4 text-primary-foreground/70 max-w-lg mx-auto">
            Explora nuestro menú completo y disfruta de la mejor comida en la comodidad de tu hogar.
          </p>
          <Link to="/menu">
            <Button 
              size="lg" 
              variant="secondary" 
              className="mt-8 h-14 px-10 rounded-full text-base font-medium"
              data-testid="cta-order-btn"
            >
              Empezar pedido
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Product Detail Modal */}
      <ProductDetail
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  );
}
